import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Lock, ShoppingBag, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DemoPaymentModal from '@/components/DemoPaymentModal';
import { formatINR, resolveImage, getErrorMessage } from '@/lib/format';

const loadScript = (src) => new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement('script');
    s.src = src; s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
});

export default function Checkout() {
    const { items, subtotal, shipping, total, clearCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('demo');
    const [demo, setDemo] = useState({ open: false, amount: 0, orderNumber: '', orderId: '' });
    const [form, setForm] = useState({
        name: '', email: '', phone: '', pincode: '', line1: '', line2: '', landmark: '', city: '', state: '',
    });

    useEffect(() => {
        api.get('/payment/config').then(({ data }) => setMode(data.mode)).catch(() => {});
    }, []);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
    }, [user]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const validate = () => {
        const required = ['name', 'email', 'phone', 'pincode', 'line1', 'city', 'state'];
        for (const r of required) if (!form[r]?.trim()) { toast.error('Please fill in all required fields.'); return false; }
        if (!/^\d{6}$/.test(form.pincode)) { toast.error('Enter a valid 6-digit pincode.'); return false; }
        if (!/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) { toast.error('Enter a valid 10-digit phone number.'); return false; }
        return true;
    };

    const finishOrder = async (orderId, verifyPayload) => {
        await api.post('/checkout/verify', verifyPayload);
        clearCart();
        toast.success('Payment successful!');
        navigate(`/order/${orderId}`);
    };

    const placeOrder = async () => {
        if (items.length === 0) { toast.error('Your cart is empty.'); return; }
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
                email: form.email,
                name: form.name,
                shipping_address: {
                    name: form.name, phone: form.phone, pincode: form.pincode,
                    line1: form.line1, line2: form.line2, landmark: form.landmark,
                    city: form.city, state: form.state,
                },
            };
            const { data } = await api.post('/checkout/create-order', payload);

            if (data.mode === 'razorpay') {
                const ok = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
                if (!ok) { toast.error('Could not load payment gateway.'); setLoading(false); return; }
                const rz = new window.Razorpay({
                    key: data.key_id,
                    amount: data.amount_paise,
                    currency: 'INR',
                    name: 'xtra',
                    description: `Order ${data.order_number}`,
                    image: '/xtra-icon.png',
                    order_id: data.razorpay_order_id,
                    prefill: { name: form.name, email: form.email, contact: form.phone },
                    theme: { color: '#1302A9' },
                    handler: async (r) => {
                        try {
                            await finishOrder(data.order_id, {
                                order_id: data.order_id,
                                razorpay_order_id: r.razorpay_order_id,
                                razorpay_payment_id: r.razorpay_payment_id,
                                razorpay_signature: r.razorpay_signature,
                            });
                        } catch { toast.error('Payment verification failed.'); setLoading(false); }
                    },
                    modal: { ondismiss: () => setLoading(false) },
                });
                rz.open();
            } else {
                // Demo mode
                setDemo({ open: true, amount: data.amount, orderNumber: data.order_number, orderId: data.order_id });
                setLoading(false);
            }
        } catch (e) {
            toast.error(getErrorMessage(e, 'Could not place order.'));
            setLoading(false);
        }
    };

    if (items.length === 0 && !demo.open) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                <div className="h-20 w-20 rounded-full bg-[var(--bg-soft)] grid place-items-center mx-auto"><ShoppingBag className="h-9 w-9 text-[var(--text-muted)]" /></div>
                <h1 className="font-display font-extrabold text-2xl mt-6">Your cart is empty</h1>
                <Link to="/shop" className="btn-primary mt-6 inline-flex h-12 px-7">Shop products</Link>
            </div>
        );
    }

    const field = (id, label, opts = {}) => (
        <div className={opts.span || ''}>
            <Label htmlFor={id} className="text-sm">{label}{opts.required !== false && <span className="text-red-500"> *</span>}</Label>
            <Input id={id} data-testid={`checkout-${id}-input`} value={form[id]} onChange={set(id)} placeholder={opts.placeholder} type={opts.type || 'text'} className="mt-1.5 h-11 rounded-xl" />
        </div>
    );

    const payLabel = mode === 'razorpay' ? 'Secured by Razorpay · UPI, Cards, Netbanking' : 'Demo checkout · no real payment is taken';

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14 pb-28 lg:pb-14">
            <h1 className="font-display font-extrabold tracking-tight text-3xl lg:text-5xl">Checkout</h1>
            {!user && (
                <p className="mt-2 text-sm text-[var(--text-muted)]">Checking out as a guest. <Link to="/login?redirect=/checkout" className="text-[var(--xtra-blue)] font-semibold hover:underline">Log in</Link> for faster checkout & order tracking.</p>
            )}

            <div className="mt-8 grid lg:grid-cols-[1fr_400px] gap-8">
                {/* Form */}
                <div data-testid="checkout-address-form" className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-white p-6">
                    <h2 className="font-display font-bold text-lg">Contact details</h2>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        {field('name', 'Full name')}
                        {field('email', 'Email', { type: 'email', placeholder: 'you@email.com' })}
                    </div>

                    <h2 className="font-display font-bold text-lg mt-8">Shipping address</h2>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        {field('phone', 'Phone', { placeholder: '10-digit mobile' })}
                        {field('pincode', 'Pincode', { placeholder: '560001' })}
                        {field('line1', 'Address line 1', { span: 'sm:col-span-2', placeholder: 'House no., building, street' })}
                        {field('line2', 'Address line 2', { span: 'sm:col-span-2', required: false, placeholder: 'Area, colony (optional)' })}
                        {field('landmark', 'Landmark', { required: false, placeholder: 'Optional' })}
                        {field('city', 'City')}
                        {field('state', 'State', { span: 'sm:col-span-2' })}
                    </div>
                </div>

                {/* Summary */}
                <div className="h-fit rounded-[var(--radius-lg)] border border-[var(--border)] bg-white p-6 lg:sticky lg:top-28">
                    <h2 className="font-display font-bold text-lg">Order summary</h2>
                    <div className="mt-4 space-y-3 max-h-64 overflow-auto no-scrollbar">
                        {items.map((it) => (
                            <div key={it.product_id} className="flex items-center gap-3">
                                <div className="relative">
                                    <img src={resolveImage(it.image)} alt={it.name} loading="lazy" decoding="async" className="h-14 w-14 rounded-lg object-contain bg-[var(--bg-soft)] p-1" />
                                    <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-[var(--xtra-blue)] text-white text-[11px] font-bold tabular-nums">{it.qty}</span>
                                </div>
                                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{it.name}</p><p className="text-xs text-[var(--text-muted)]">{it.size}</p></div>
                                <span className="text-sm font-semibold tabular-nums">{formatINR(it.price * it.qty)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-3 text-sm">
                        <div className="flex justify-between"><span className="text-[var(--text-muted)]">Subtotal</span><span className="font-medium tabular-nums">{formatINR(subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-muted)]">Shipping</span><span className="font-medium tabular-nums">{shipping === 0 ? 'FREE' : formatINR(shipping)}</span></div>
                        <div className="pt-3 border-t border-[var(--border)] flex justify-between text-base font-display font-extrabold"><span>Total</span><span data-testid="checkout-order-total" className="tabular-nums">{formatINR(total)}</span></div>
                    </div>
                    <button data-testid="checkout-pay-now-button" onClick={placeOrder} disabled={loading} className="btn-primary w-full h-12 mt-5 disabled:opacity-80">
                        {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</> : <><Lock className="h-4 w-4" /> Pay {formatINR(total)}</>}
                    </button>
                    <p className="mt-3 text-xs text-center text-[var(--text-muted)] inline-flex items-center justify-center gap-1.5 w-full"><ShieldCheck className="h-3.5 w-3.5" /> {payLabel}</p>
                </div>
            </div>

            {/* Mobile sticky pay bar */}
            <div className="sticky-bar lg:hidden">
                <div className="px-4 py-3 flex items-center gap-3">
                    <div className="min-w-0">
                        <p className="text-xs text-[var(--text-muted)] leading-none">Total</p>
                        <p className="font-display font-extrabold text-lg tabular-nums leading-tight">{formatINR(total)}</p>
                    </div>
                    <button data-testid="checkout-pay-now-sticky" onClick={placeOrder} disabled={loading} className="btn-primary flex-1 h-12 disabled:opacity-80">
                        {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</> : <><Lock className="h-4 w-4" /> Pay now</>}
                    </button>
                </div>
            </div>

            <DemoPaymentModal
                open={demo.open}
                amount={demo.amount}
                orderNumber={demo.orderNumber}
                onClose={() => setDemo((d) => ({ ...d, open: false }))}
                onPaid={async () => {
                    try { await finishOrder(demo.orderId, { order_id: demo.orderId, demo: true }); }
                    catch { toast.error('Something went wrong.'); }
                }}
            />
        </div>
    );
}
